/**
 * Keeps Sentry to error reporting only.
 *
 * Counsel (2026-10-29): Sentry may run before cookie consent because it only
 * reports errors, as long as reports don't carry GraphQL operations or URL
 * parameters. Explorer and Sandbox URLs can hold the visitor's operation in
 * the query string (`explorerURLState`, GET `query`/`variables`), so every URL
 * loses its query string and fragment (and its path tokens, via
 * `redactSensitiveUrl`). Console breadcrumbs are off in `initSentry`, and the
 * Explorer no longer attaches its document to crash reports.
 *
 * Must never throw: a throwing hook loses the report.
 */
import type { Breadcrumb, ErrorEvent } from '@sentry/react';

import { redactSensitiveUrl } from './redactSensitiveUrl';

export function stripUrlParams<T extends string | undefined>(url: T): T {
  if (!url) return url;
  return redactSensitiveUrl(url).replace(/[?#].*$/, '') as T;
}

/* eslint-disable no-param-reassign */
export function scrubSentryEvent(event: ErrorEvent): ErrorEvent {
  try {
    if (event.request) {
      event.request.url = stripUrlParams(event.request.url);
      delete event.request.query_string;
      if (event.request.headers?.Referer) {
        event.request.headers.Referer = stripUrlParams(
          event.request.headers.Referer,
        );
      }
    }
    event.breadcrumbs = event.breadcrumbs?.map(scrubSentryBreadcrumb);
  } catch {
    // never lose an error report over a malformed event
  }
  return event;
}

export function scrubSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const { data } = breadcrumb;
  if (data) {
    ['url', 'from', 'to'].forEach((key) => {
      if (typeof data[key] === 'string') data[key] = stripUrlParams(data[key]);
    });
  }
  return breadcrumb;
}
/* eslint-enable no-param-reassign */
